// src/components/patient/booking/DateOfBirthDialog.jsx
//
// Asks for the patient's date of birth without leaving the booking.
//
// Services with an age limit cannot be booked until a date of birth is on the
// patient's profile. Sending them away to Settings loses the service, the
// dates and the chosen time, so the same value is captured here and saved to
// the same place Settings writes to.
//
// It is a real dialog, never window.prompt: focus moves in, Tab stays inside,
// Escape cancels unless a save is running, and focus returns to the opener.

import React, { useEffect, useRef, useState } from "react";

import { dateOfBirthError, earliestISO, todayISO } from "../../../utils/dateOfBirth";
import { patientSettingsApi } from "../../../services/patientSettings";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function DateOfBirthDialog({ open, ageLimitText, initialValue = "", onSaved, onCancel }) {
  const ref = useRef(null);
  const inFlight = useRef(false);
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setValue(initialValue);
    setError("");
    const opener = document.activeElement;
    ref.current?.querySelector(FOCUSABLE)?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        if (!inFlight.current) onCancel?.();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = Array.from(ref.current?.querySelectorAll(FOCUSABLE) || []);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (opener?.focus) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  async function submit(event) {
    event.preventDefault();
    const problem = dateOfBirthError(value);
    setError(problem);
    if (problem || inFlight.current) return;

    inFlight.current = true;
    setSaving(true);
    try {
      await patientSettingsApi.setDateOfBirth(value);
      await onSaved?.(value);
    } catch (caught) {
      // The typed date stays, so a correction is one edit away.
      setError(caught?.message || "Your date of birth could not be saved. Please try again.");
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/40 px-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dob-dialog-title"
        data-testid="date-of-birth-dialog"
        className="w-full max-w-md rounded-3xl border border-border bg-white p-6 shadow-soft"
      >
        <h2 id="dob-dialog-title" className="text-lg font-semibold">Add your date of birth</h2>
        <p className="mt-2 text-sm text-muted">
          {ageLimitText
            ? `This service is limited to ${ageLimitText.toLowerCase()}, so we need your date of birth to check that you can book it.`
            : "This service has an age requirement, so we need your date of birth to check that you can book it."}
        </p>

        <form onSubmit={submit} className="mt-4">
          <label htmlFor="dob-dialog-input" className="text-sm font-semibold">
            Date of birth
          </label>
          <input
            id="dob-dialog-input"
            type="date"
            value={value}
            max={todayISO()}
            min={earliestISO()}
            disabled={saving}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={error ? "dob-dialog-error" : "dob-dialog-help"}
            onChange={(event) => setValue(event.target.value)}
            className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
          />
          {error ? (
            <p id="dob-dialog-error" role="alert" className="mt-2 text-sm font-semibold text-rose-600">
              {error}
            </p>
          ) : (
            <p id="dob-dialog-help" className="mt-2 text-xs text-muted">
              Saved to your profile and used only to work out your age. You can change it later in Settings.
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => (saving ? null : onCancel?.())}
              disabled={saving}
              className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              aria-busy={saving || undefined}
              className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save and continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
